/**
 * Gedeelde helpers + design-tokens voor de PDF-export modules.
 *
 * Splitsing uit `treePdfExport.ts` (#525 refactor): dit module bevat de pure,
 * stateless utilities die door `treePdfCards.ts`, `treePdfLinks.ts` en
 * `treePdfAssembly.ts` worden hergebruikt. Geen imports vanuit de andere
 * tree-PDF-modules — dit is het fundament zonder cyclische dependencies.
 */

import type { Person, CardLevel } from '../types.ts';

// ── Card-geometrie (PDF/SVG-ruimte — bewust ONAFHANKELIJK van TreeCanvas) ───
// Deze maten gelden alleen in de PDF/SVG-export-ruimte en spiegelen de
// TreeCanvas DOM-constants NIET (bijv. COMPACT_NODE_HEIGHT: PDF 160 vs
// TreeCanvas 172; NODE_WIDTH: PDF 185 vs TreeCanvas 230). Die drift is bewust
// (NC #600/#1151): de export rekent in eigen metrics, gesynchroniseerd houden
// is geen doel. Wijzig ze dus onafhankelijk van elkaar.
export const NODE_WIDTH = 185;
export const NODE_HEIGHT = 96;
export const COMPACT_NODE_WIDTH = 111;
export const COMPACT_NODE_HEIGHT = 160;
export const HEADER_HEIGHT = 6;
export const AVATAR_RADIUS = 18;

// ── Kleuren (matchen tree-v2.css design tokens) ─────────────────────────────
export const COLOR_MALE = '#1B365D';
export const COLOR_FEMALE = '#9B111E';
export const COLOR_UNKNOWN = '#888888';
export const COLOR_PROBAND = '#D4AF37';
export const COLOR_TEXT_MAIN = '#1A2F23';
export const COLOR_TEXT_MUTED = '#6B7B73';
export const COLOR_TEXT_FAINT = '#9CA39E';
export const COLOR_CARD_BG = '#FFFFFF';
export const COLOR_CARD_BORDER = '#E5E5E0';
export const COLOR_GEN_BAND = '#F4F1EA';
export const COLOR_LINK = '#BCC6B8';

/** Sosa-pill text-kleuren per gender (spec §8 fan-chart). */
export const SOSA_COLOR_MALE = '#1B365D'; // heritage-male
export const SOSA_COLOR_FEMALE = '#9B111E'; // heritage-ruby
export const SOSA_COLOR_UNKNOWN = '#6B5215'; // gold-shade fallback

/** Lokale, deterministische SVG-builder: pure DOM, geen D3 nodig. */
export function svgEl(tag: string, attrs: Record<string, string | number | undefined>): SVGElement {
  const el = document.createElementNS('http://www.w3.org/2000/svg', tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === undefined || v === null) continue;
    // Audit P0.2 root-cause: callers passeren `xmlns` als attr, maar
    // createElementNS heeft die namespace al gezet. Dubbel `xmlns="..."`
    // produceert malformed XML — strict image-loaders (Chromium <img src=svg>)
    // weigeren de SVG → blank canvas in PNG-export. Skip de redundante
    // xmlns attribute hier centraal i.p.v. elke caller te wijzigen.
    if (k === 'xmlns') continue;
    el.setAttribute(k, String(v));
  }
  return el;
}

export function escapeText(s: string | undefined): string {
  if (!s) return '';
  return s.replace(/[<>&]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' })[c] || c);
}

export function genderColor(gender: Person['gender'] | undefined, isProband: boolean): string {
  if (isProband) return COLOR_PROBAND;
  if (gender === 'M') return COLOR_MALE;
  if (gender === 'F') return COLOR_FEMALE;
  return COLOR_UNKNOWN;
}

export function getYear(dateStr?: string): string {
  if (!dateStr) return '';
  // Item 13 — accepteer 1-4 digit jaartallen (Middeleeuwen: "850") via een
  // zoektocht naar de LANGSTE digit-sequence van 4..1 chars. Voorkomt dat
  // "5 mei 1234" → "5" matched: probeer eerst 4-digit, dan 3, dan 2, dan 1.
  // 5+ digit blokken (future dates >9999, BCE >=10000) gebruiken alleen
  // de eerste 4 digits — bewust gekozen omdat genealogie-rendering geen
  // 5-digit jaartallen accepteert.
  for (const len of [4, 3, 2, 1]) {
    const re = new RegExp(`(?<!\\d)\\d{${len}}(?!\\d)`);
    const m = dateStr.match(re);
    if (m) return m[0];
  }
  return '';
}

/** Strip de UI-`#`-prefix uit plaatsnamen (de hashtag is een UI-token, hoort niet in een
 * archief-document). Trimt ook trailing whitespace en placeholder-prefixes. */
export function formatPlaceForPdf(place: string | undefined): string {
  if (!place) return '';
  return place.replace(/^[#@]\s*/, '').trim();
}

/** Geef alleen het eerste comma-segment van een plaatsnaam terug, zonder provincie/land.
 * "Steenderen, Gelderland, Nederland" → "Steenderen". Strikt voor PDF-card vital-regels
 * waar ruimte krap is. UI-prefixes (#/@) worden gestript. */
export function formatPlaceFirstSegment(place: string | undefined): string {
  if (!place) return '';
  const cleaned = place.replace(/^[#@]\s*/, '').trim();
  const commaIdx = cleaned.indexOf(',');
  return commaIdx > 0 ? cleaned.substring(0, commaIdx).trim() : cleaned;
}

/** Convert hex `#RRGGBB` naar `[R, G, B]` voor jsPDF setFillColor/setTextColor.
 * Geeft `[0,0,0]` terug bij invalid input. */
export function hexToRgb(hex: string): [number, number, number] {
  const m = hex.match(/^#?([a-fA-F\d]{2})([a-fA-F\d]{2})([a-fA-F\d]{2})$/);
  if (!m) return [0, 0, 0];
  return [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)];
}

/** Bereken hoeveel chars op één regel passen in `innerWidthPx` bij gegeven font-size.
 *
 * #525 — Gerecalibreerd voor Plus Jakarta Sans (was Helvetica 0.62).
 * Bold-weights zijn iets breder dan regular, daarom een aparte factor:
 *   regular (400–600) → 0.68
 *   bold    (700–800) → 0.70
 *
 * Factors zijn empirisch bepaald op rendering in svg2pdf met de embedded
 * Plus Jakarta Sans subsets (zie `fontEmbed.ts`). */
export function maxCharsPerLineFor(
  innerWidthPx: number,
  fontSize: number,
  isBold: boolean = false
): number {
  const factor = isBold ? 0.7 : 0.68;
  return Math.max(4, Math.floor(innerWidthPx / (fontSize * factor)));
}

/**
 * Splits tekst over meerdere regels op basis van max chars per regel. Splitst
 * voorkeur op spaties; lange enkele woorden krijgen een hard-split om binnen de
 * cell te blijven. Stamboom-rapport vereist dat namen NIET worden afgebroken
 * met '…' — daarom kiezen we voor wrap.
 */
export function wrapTextLines(text: string, maxCharsPerLine: number): string[] {
  if (text.length <= maxCharsPerLine) return [text];
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length === 0) return [text];

  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    if (word.length > maxCharsPerLine) {
      // Hard-split very long single word
      if (current) {
        lines.push(current);
        current = '';
      }
      let remaining = word;
      while (remaining.length > maxCharsPerLine) {
        lines.push(remaining.slice(0, maxCharsPerLine));
        remaining = remaining.slice(maxCharsPerLine);
      }
      current = remaining;
      continue;
    }
    if (!current) {
      current = word;
    } else if ((current + ' ' + word).length <= maxCharsPerLine) {
      current += ' ' + word;
    } else {
      lines.push(current);
      current = word;
    }
  }
  if (current) lines.push(current);
  return lines;
}

/** Voeg `text` toe als één-of-meer <text> elementen met wrap op spaties.
 * Bij overlange enkele woorden wordt hard-gesplit. Retourneert het aantal regels. */
export function appendWrappedText(
  parent: SVGElement,
  text: string,
  startX: number,
  startY: number,
  maxCharsPerLine: number,
  attrs: Record<string, string | number>,
  lineHeightPx: number
): number {
  const safe = escapeText(text);
  const lines = wrapTextLines(safe, maxCharsPerLine);
  for (let i = 0; i < lines.length; i++) {
    const t = svgEl('text', {
      ...attrs,
      x: startX,
      y: startY + i * lineHeightPx,
    });
    t.textContent = lines[i];
    parent.appendChild(t);
  }
  return lines.length;
}

// ── D4 design-taal (#525, spec 2) ────────────────────────────────────────────
/** Kleur-palet shape voor het D4 ontwerp. Drie varianten implementeren dit
 * contract: D4_COLORS (heritage), D4_COLORS_BW (grijs/zwart), D4_COLORS_MINIMAL
 * (rustige tinten). Alle hex-waarden hardcoded zodat svg2pdf consistent RGB
 * ziet (geen CSS-variabele resolutie tijdens export). */
export interface D4Palette {
  bgCard: string;
  border: string;
  textMain: string;
  textMuted: string;
  goldPill: string;
  goldText: string;
  male: string;
  female: string;
  ruby: string;
  /** Spousal-line kleur (gouden lijn in color-mode). */
  spousal: string;
  /** #1590 — kaartvorm (schermstijl in kleur): hoekstraal, randdikte, strookhoogte. */
  kaartRx: number;
  randDikte: number;
  strookHoogte: number;
  /** #1590 — strook bij onbekend geslacht (was: de kleur van man). */
  onbekend: string;
  /** #1590 — vlak achter het Sosa-nummer ('transparent' = geen vlak). */
  sosaVlak: string;
}

export const D4_COLORS: D4Palette = {
  bgCard: '#FFFFFF',
  // #1590 — zachte rand zoals de kaart op het scherm (was forest #1A2F23).
  border: '#CFD6CC',
  textMain: '#1A2F23',
  textMuted: '#6B7F73',
  goldPill: '#D4AF37',
  goldText: '#8B7420',
  male: '#1B365D',
  female: '#9B111E',
  ruby: '#9B111E',
  spousal: '#D4AF37',
  // #1590 — de kaart van de Stamboom-pagina: zacht, afgerond, dikkere strook.
  kaartRx: 10,
  randDikte: 1.2,
  strookHoogte: 5,
  onbekend: '#9CA3AF',
  sosaVlak: '#F7F1DB',
};

/** B&W variant: zwarte border + tekst, grijze sosa-pill, donker/lichtgrijs voor
 * gender. Bedoeld voor printers zonder kleur of voor neutrale archief-uitprints
 * waar kleur geen extra informatie mag toevoegen. */
export const D4_COLORS_BW: D4Palette = {
  bgCard: '#ffffff',
  border: '#000000',
  textMain: '#000000',
  textMuted: '#555555',
  goldPill: '#e5e5e5', // grijs ipv goud
  goldText: '#000000',
  male: '#333333',
  female: '#777777',
  ruby: '#000000',
  spousal: '#666666', // grijs ipv goud
  kaartRx: 6,
  randDikte: 2,
  strookHoogte: 3,
  onbekend: '#999999',
  sosaVlak: 'transparent',
};

/** Minimal variant: heel rustig — geen pill-vlak (transparant), monochroom
 * forest voor tekst/gender. Gold blijft alleen voor proband-accent (heel
 * subtiel). Geschikt voor moderne, minimalistische exports. */
export const D4_COLORS_MINIMAL: D4Palette = {
  bgCard: '#ffffff',
  border: '#c4c4c4',
  textMain: '#1a2f23',
  textMuted: '#8a9a8e',
  goldPill: 'transparent', // geen pill-vlak in minimal
  goldText: '#1a2f23',
  male: '#1a2f23', // monochroom forest
  female: '#1a2f23',
  ruby: '#9b111e',
  spousal: '#c4c4c4', // zelfde tint als border, niet meer gold-accent
  kaartRx: 6,
  randDikte: 2,
  strookHoogte: 3,
  onbekend: '#1a2f23',
  sosaVlak: 'transparent',
};

/** Kies kleur-palet o.b.v. styleVariant. Default = 'color' (D4_COLORS). */
export function colorsForVariant(variant: 'color' | 'bw' | 'minimal' | undefined): D4Palette {
  if (variant === 'bw') return D4_COLORS_BW;
  if (variant === 'minimal') return D4_COLORS_MINIMAL;
  return D4_COLORS;
}

/** Subset van toggles die `computeCardHeight` + `buildCardSvg` nodig hebben.
 *
 * Gedeeld tussen modules zodat zowel `treePdfCards.ts` (rendering) als
 * `treePdfAssembly.ts` (compactify) dezelfde shape kunnen importeren zonder
 * cyclische dependency. */
export interface CardDisplayOpts {
  fullDates?: boolean;
  showDates?: boolean;
  showJobs?: boolean;
  showLocations?: boolean;
  /** #525 Phase 8 — kleurenschema-variant, ook bij legacy-callers. */
  styleVariant?: 'color' | 'bw' | 'minimal';
}

// — Layout-redesign constanten (rev3.4) —
// Refs: docs/superpowers/plans/2026-05-18-export-layout-redesign.md Task 1.1

export const MAX_NODES = 2048;
export const MIN_FONT_PT = 9;
export const SAFETY_MARGIN = 0.97;
export const MIN_RATIO = 0.85;
export const REDACTED_PLACEHOLDER_WIDTH = 130;
export const MIN_SPOUSE_GAP_REG = 10;
export const MIN_SPOUSE_GAP_COMPACT = 8;
export const MIN_SIBLING_GAP_REG = 50;
export const MIN_SIBLING_GAP_COMPACT = 10;
export const GEN_GAP_REG = 36;
export const GEN_GAP_COMPACT = 24;
export const PARTNER_GROUP_GAP = 30;
export const H_GAP_REG = 50;
export const H_GAP_COMPACT = 10;
export const V_GAP = 40;

// ── Card-width per cardLevel (matcht treePdfCards.ts DETAILED/STANDARD/COMPACT.w) ──
// Verplaatst van useTreeLayoutV2.ts (#525 H.1 V1 fix) zodat zowel V1 als V2
// dezelfde render-width-aware slotPitch-formule kunnen gebruiken zonder
// circulaire imports. V2 re-exporteert deze symbolen voor backwards-compat.
export const CARD_WIDTH_DETAILED = 220;
export const CARD_WIDTH_STANDARD = 180;
// Audit P1.3: bumped 130 → 160 voor betere leesbaarheid in 6-gen pedigrees.
// Match met treePdfCards.ts COMPACT.w — beide waardes MOETEN gelijk lopen.
export const CARD_WIDTH_COMPACT = 160;

/**
 * Bepaal de breedte van een kaart voor een gegeven cardLevel.
 *
 * Spec §5.1: `computeCardWidthForLevel(level, selectedFields)`. `selectedFields`
 * is voorlopig informatief — de drie levels hebben elk een vaste basis-breedte
 * (220/180/130) die gelijk loopt met `DETAILED.w`/`STANDARD.w`/`COMPACT.w` in
 * `treePdfCards.ts`. Toekomstige uitbreiding kan extra breedte toevoegen voor
 * speciale field-combinaties zonder de signature te breken.
 */
export function computeCardWidthForLevel(level: CardLevel, _selectedFields: string[]): number {
  if (level === 'detailed') return CARD_WIDTH_DETAILED;
  if (level === 'standard') return CARD_WIDTH_STANDARD;
  if (level === 'compact') return CARD_WIDTH_COMPACT;
  // 'micro' fallback: gebruik compact-breedte
  return CARD_WIDTH_COMPACT;
}

/** Rotated outer-ring card-dimensies. Card is vertical 90×180 op SVG, na 90° rotatie
 * occupeert het 90 wide × 180 tall in de tree-layout. */
export const GEN5_CARD_WIDTH = 90;
export const GEN5_CARD_HEIGHT = 180;

/**
 * Pair-width = child-width math (spec §4).
 *
 * Bouwt bottom-up vanaf gen-5 (W₅=40, intra-pair gap=2 → pair-width=82). Elke
 * gen-N card-width = gen-N+1 pair-width zodat het paar visueel gecentreerd is
 * boven hun kind. Proband (gen-1) heeft eigen Detailed-size (240) onafhankelijk
 * van pair-cascade.
 */
export interface PairWidthMap {
  /** Card-width in px per generatie (1 = proband). */
  cardWidthByGen: Record<number, number>;
  /** Intra-pair gap (vader↔moeder) in px per generatie. */
  intraPairGapByGen: Record<number, number>;
  /** True wanneer top-gen 90° geroteerd moet worden (5 gen op 1 A4). */
  rotateTopGen: boolean;
}

export function computePairWidthByGen(maxGen: number): PairWidthMap {
  const cardWidthByGen: Record<number, number> = {};
  const intraPairGapByGen: Record<number, number> = {};

  const topGen = Math.max(maxGen, 2);
  // #525 (post-Plan-A): uniform card-widths per gen-level i.p.v. cascade.
  // Outer-ring rotated 90×180 ALLEEN wanneer slider >= 4 generaties (= topGen >= 5).
  // Bij slider 3 of lager: geen rotation, alle cards horizontaal standard/detailed.
  const shouldRotate = topGen >= 5;
  for (let g = 1; g <= topGen; g++) {
    if (g === topGen && shouldRotate) {
      cardWidthByGen[g] = GEN5_CARD_WIDTH; // rotated outer-ring (90)
      intraPairGapByGen[g] = 2; // couple-pairing: vader+moeder tight tegen elkaar
    } else if (g >= 3) {
      cardWidthByGen[g] = CARD_WIDTH_STANDARD; // 180
      intraPairGapByGen[g] = 20;
    } else {
      cardWidthByGen[g] = CARD_WIDTH_DETAILED; // 220
      intraPairGapByGen[g] = g === 1 ? 0 : 40;
    }
  }

  return {
    cardWidthByGen,
    intraPairGapByGen,
    rotateTopGen: shouldRotate,
  };
}
